/**
 * Promoções ML — a tela do painel validado pela Mariana em 18/09/2026, com as 7 abas:
 * Robô (ex-Aprovações) · Promoções · Por anúncio · Análise · Alertas · Simulador · Histórico.
 *
 * Regras de cálculo: ./promocoes/logic.ts. Chamadas HTTP: ./promocoes/promocoesService.ts.
 * Estado compartilhado: ./promocoes/ctx.tsx. Peças visuais: ./promocoes/ui.tsx.
 *
 * Três modos, e o selo do topo diz qual: PREVIEW (serviço fora do ar — nada é enviado ao ML),
 * TESTE (serviço com PROMO_DRY_RUN=1 — o fluxo inteiro roda, a escrita para antes do ML) e
 * ESCRITA REAL (confirmação em dois cliques, tudo em escritas_promo.log).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, Card, CardContent, CircularProgress, Grid, Stack, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material'
import { DomainPageShell } from '../components/DomainPageShell'
import { KpiCard } from '../components/KpiCard'
import { PropostaIaCard, type PropostaIaState } from '../components/PropostaIaCard'
import { useRegisterPanelInsights } from '../state/PanelInsightsContext'
import { carregaPromocoes, subtituloFonte, type FonteInfo } from './promocoes/fonte'
import {
  ALVO_PADRAO, CONTAS, ROTULO_BASE, aplicaStatusLocal, explicaBase, kpis, paramsParaRemover, payloadAtivar, selKey,
  type Anuncio, type BaseImposto, type BasePromocoes, type FilaDecisoes, type Promocao, type StatusLocal, type StatusPromo,
} from './promocoes/logic'
import { ativarPromo, estadoMotor, fetchFila, fetchFreteReal, fetchPromoStatus, fetchPromocoesAnalise, removerPromo, rodarMotor, type PromoStatus, type ResultadoEscrita } from './promocoes/promocoesService'
import { PromoCtx, type PromoCtxValue } from './promocoes/ctx'
import { ModalParticipar } from './promocoes/ModalParticipar'
import { AbaAprovacoes } from './promocoes/AbaAprovacoes'
import { AbaPromocoes } from './promocoes/AbaPromocoes'
import { AbaAnuncio } from './promocoes/AbaAnuncio'
import { AbaAnalise } from './promocoes/AbaAnalise'
import { AbaAlertas } from './promocoes/AbaAlertas'
import { AbaSimulador } from './promocoes/AbaSimulador'
import { AbaHistorico } from './promocoes/AbaHistorico'
import { RodapeRegras } from './promocoes/RodapeRegras'
import { usePanelAccess } from '../permissions/usePanelAccess'

type Aba = 'aprovacoes' | 'promocoes' | 'anuncio' | 'analise' | 'alertas' | 'simulador' | 'historico'
const ABAS: Array<[Aba, string]> = [
  ['aprovacoes', 'Robô'], ['promocoes', 'Promoções'], ['anuncio', 'Por anúncio'], ['analise', 'Análise'],
  ['alertas', 'Alertas'], ['simulador', 'Simulador'], ['historico', 'Histórico'],
]

const lerLS = (k: string) => { try { return localStorage.getItem(k) } catch { return null } }
const gravarLS = (k: string, v: string) => { try { localStorage.setItem(k, v) } catch { /* sem storage */ } }

function ModoTag({ live }: { live: PromoStatus | null }) {
  const t = useTheme()
  const teste = live?.live && live.dry_run
  const cor = live?.live ? (teste ? t.palette.warning.main : t.palette.error.main) : t.palette.text.disabled
  const txt = live?.live
    ? teste ? `🧪 AMBIENTE DE TESTE — simula, nada vai para o ML (${live.nickname || live.user_id || ''})` : `● escrita real — ${live.nickname || live.user_id || ''}`
    : '● preview — nada é escrito na API'
  return (
    <Box component="span" title={live?.motivo || ''} sx={{ display: 'inline-block', fontSize: 11, borderRadius: 99, px: 1.25, py: 0.2, border: `1px ${live?.live ? 'solid' : 'dashed'} ${cor}`, color: cor, fontWeight: live?.live ? 700 : 400, whiteSpace: 'nowrap' }}>{txt}</Box>
  )
}

export function PromocoesPage() {
  const { canWrite } = usePanelAccess()
  const [baseDados, setBaseDados] = useState<BasePromocoes | null>(null)
  const [fonte, setFonte] = useState<FonteInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [contaId, setContaId] = useState<string>(CONTAS[0].id)
  // 30/09/2026, ela: "a margem que deve constar em todos os painéis como referência de decisão é a
  // MARGEM DE CONTRIBUIÇÃO AJUSTADA". A tela SEMPRE abre na ajustada; a oficial é só comparação e
  // não fica gravada no navegador (antes ficava, e a tela podia abrir na oficial sem ela perceber).
  const [base, setBase] = useState<BaseImposto>('ajustada')
  const [alvo, setAlvo] = useState<number>(() => { const g = parseFloat(lerLS('fiesta_promo_alvo_mc') ?? ''); return !Number.isNaN(g) && g >= 0 && g <= 90 ? g : ALVO_PADRAO })
  const [alvoTxt, setAlvoTxt] = useState<string>(String(alvo))
  const [aba, setAba] = useState<Aba>('promocoes')
  const [mlbSel, setMlbSel] = useState<string | null>(null)
  const [live, setLive] = useState<PromoStatus | null>(null)
  const [fila, setFila] = useState<FilaDecisoes | null>(null)
  const [cmvManual, setCmvManualMap] = useState<Map<string, number>>(new Map())
  const [statusLocal, setStatusLocal] = useState<Map<string, StatusLocal>>(new Map())
  const [sel, setSel] = useState<Set<string>>(new Set())
  const [pickerVal, setPickerVal] = useState<Map<string, number>>(new Map())
  const [modal, setModal] = useState<{ a: Anuncio; p: Promocao } | null>(null)
  const [leituraIa, setLeituraIa] = useState<PropostaIaState>({ loading: false })
  const [freteCache] = useState(() => new Map<string, number | null>())
  // «Atualizar agora» do cabeçalho (02/10/2026): relê a API + motor + republica, em qualquer aba.
  const [atualizando, setAtualizando] = useState<string | null>(null)
  const [avisoAtualizacao, setAvisoAtualizacao] = useState<string | null>(null)

  /**
   * `silencioso` (02/10/2026): recarrega o .json.gz sem derrubar a tela no spinner — usado quando o
   * serviço republica (botão Atualizar, robô, cron). Antes toda recarga passava por loading=true e a
   * tela inteira piscava; por isso ninguém recarregava e o painel parecia "estático".
   */
  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) { setLoading(true); setError(null) }
    try {
      const { base: b, fonte: f } = await carregaPromocoes()
      setBaseDados(b); setFonte(f)
      if (b.fila) setFila(b.fila)
      const ids = Object.keys(b.contas)
      if (ids.length && !b.contas[contaId]) setContaId(ids[0])
      if (silencioso) setAvisoAtualizacao(`painel atualizado às ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`)
    } catch (e) {
      if (silencioso) { setAvisoAtualizacao(`não consegui recarregar o painel: ${e instanceof Error ? e.message : String(e)}`); return }
      setError(e instanceof Error ? e.message : 'Falha ao carregar promoções.')
      setBaseDados(null); setFonte(null)
    } finally { if (!silencioso) setLoading(false) }
  }, [contaId])
  const recarregarSilencioso = useCallback(() => carregar(true), [carregar])

  useEffect(() => { void carregar() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // o serviço de escrita: há token válido para esta conta? com ele no ar, a fila vem do disco
  useEffect(() => {
    let cancel = false
    setLive(null)
    void fetchPromoStatus(contaId)
      .then(async (st) => {
        if (cancel) return
        setLive(st)
        if (st.live) {
          const f = await fetchFila()
          if (f && !cancel) {
            setFila(f)
            // o que já foi escrito no ML depois da fila (por ela ou pelo robô) some do painel na hora,
            // sem esperar a coleta das 02:30 — pedido de 24/09/2026
            if (f.executadas?.length) setStatusLocal((m) => { const n = new Map(m); for (const e of f.executadas || []) n.set(`${e.mlb}|${e.promotion_id || e.promotion_type}`, { status: e.acao === 'remover' ? 'candidate' : 'started', preview: false }); return n })
          }
        }
      })
      .catch(() => { if (!cancel) setLive({ live: false, motivo: 'serviço de escrita não encontrado', user_id: contaId }) })
    return () => { cancel = true }
  }, [contaId])

  // ao trocar de conta, limpa o que é da sessão
  useEffect(() => { setSel(new Set()); setStatusLocal(new Map()); setPickerVal(new Map()); setMlbSel(null) }, [contaId])

  const metaConta = CONTAS.find((c) => c.id === contaId) ?? CONTAS[0]
  const contaCrua = baseDados?.contas?.[contaId] ?? null
  const conta = useMemo(() => (contaCrua ? aplicaStatusLocal(contaCrua, statusLocal) : null), [contaCrua, statusLocal])
  const escreve = !!live?.live && canWrite('promocoes')
  const travaMargemAlta = fila?.regras.revisao_margem_alta ?? true
  const kpiList = useMemo(() => (conta ? kpis(conta, base, cmvManual, travaMargemAlta) : []), [conta, base, cmvManual, travaMargemAlta])

  // Auto-atualização (02/10/2026): o cron republica o painel 3× ao dia e cada rodada do robô também;
  // quem está com a tela aberta via o dado velho até dar F5. A cada 60 s pergunta ao serviço a hora da
  // fila; mudou → recarrega o .json.gz em silêncio. Enquanto um "Reler a API" de alguém estiver rodando,
  // mostra o passo no cabeçalho (sem disparar outro).
  useEffect(() => {
    if (!escreve) return
    let vivo = true
    const tick = async () => {
      try {
        const [f, st] = await Promise.all([fetchFila(), estadoMotor().catch(() => null)])
        if (!vivo) return
        if (st?.rodando && !atualizando) setAvisoAtualizacao(`${st.passo ?? 'atualizando'} (${st.por ?? '?'})`)
        if (f && fila && f.gerado_em !== fila.gerado_em) { setFila(f); await carregar(true) }
      } catch { /* serviço fora: a próxima batida tenta de novo */ }
    }
    const id = window.setInterval(() => { void tick() }, 60000)
    return () => { vivo = false; window.clearInterval(id) }
  }, [escreve, fila, atualizando, carregar]) // eslint-disable-line react-hooks/exhaustive-deps

  const atualizarAgora = async () => {
    if (atualizando) return
    setAtualizando('iniciando…'); setAvisoAtualizacao(null)
    try {
      const r = await rodarMotor((p) => setAtualizando(p))
      const f = await fetchFila()
      if (f) setFila(f)
      if (r.publicado) await carregar(true)
      const api = r.atualizacao ? (r.atualizacao.ok ? 'API relida' : 'API NÃO relida (veja o Histórico)') : 'sem releitura'
      setAvisoAtualizacao(r.ok ? `${api} · motor rodou · ${r.publicado ? 'painel republicado' : 'painel NÃO republicado'} · ${Math.round(r.segundos)} s` : `falhou: ${(r.saida || '').slice(-200)}`)
    } catch (e) { setAvisoAtualizacao(`erro: ${e instanceof Error ? e.message : String(e)}`) }
    setAtualizando(null)
  }


  const marcar = useCallback((k: string, status: StatusPromo, preview: boolean) => setStatusLocal((m) => { const n = new Map(m); n.set(k, { status, preview }); return n }), [])
  const desmarcar = useCallback((k: string) => setStatusLocal((m) => { const n = new Map(m); n.delete(k); return n }), [])
  // status null = desmarcar. Um único setState para o lote inteiro (ver ctx.tsx).
  const marcarLote = useCallback((itens: Array<{ k: string; status: StatusPromo | null; preview: boolean }>) => {
    if (!itens.length) return
    setStatusLocal((m) => {
      const n = new Map(m)
      for (const { k, status, preview } of itens) { if (status == null) n.delete(k); else n.set(k, { status, preview }) }
      return n
    })
  }, [])

  const ativar = useCallback(async (a: Anuncio, p: Promocao, preco: number, unidades?: number | null): Promise<ResultadoEscrita> => {
    const r = await ativarPromo(payloadAtivar(contaId, a, p, preco, new Date(), unidades))
    if (r.ok) marcar(selKey(a, p), 'started', false)
    return r
  }, [contaId, marcar])
  const remover = useCallback(async (a: Anuncio, p: Promocao): Promise<ResultadoEscrita> => {
    const r = await removerPromo({ conta: contaId, mlb: a.mlb, promotion_type: p.type, ...paramsParaRemover(p) })
    if (r.ok) marcar(selKey(a, p), 'candidate', false)
    return r
  }, [contaId, marcar])
  const freteReal = useCallback(async (mlb: string, preco: number) => {
    const k = `${mlb}|${preco.toFixed(2)}`
    if (freteCache.has(k)) return freteCache.get(k) ?? null
    const v = await fetchFreteReal(contaId, mlb, preco)
    freteCache.set(k, v)
    return v
  }, [contaId, freteCache])

  const carregarLeitura = useCallback(async () => {
    setLeituraIa((prev) => ({ ...prev, loading: true, erro: null }))
    const data = await fetchPromocoesAnalise(contaId)
    if (!data.ok) { setLeituraIa({ loading: false, texto: null, fonte: 'regras', erro: data.erro ?? 'leitura indisponível', modelo: null }); return }
    setLeituraIa({ loading: false, texto: data.texto || data.leitura || null, modelo: data.modelo, fonte: data.fonte, cached: data.cached })
  }, [contaId])
  useEffect(() => { if (conta) void carregarLeitura() }, [conta?.conta, carregarLeitura]) // eslint-disable-line react-hooks/exhaustive-deps

  // leitura vem pronta do serviço (/promocoes/analise): foco personalizado ainda não se aplica
  useRegisterPanelInsights({
    titulo: 'Promoções',
    verdade: 'Verdade = números da tela (KPIs e tabelas). Proposta = leitura da IA — revise antes de agir.',
    estado: leituraIa,
    recarregar: carregarLeitura,
    aceitaFoco: false,
  })

  const ctxValue: PromoCtxValue | null = conta ? {
    contaId, contaCurto: metaConta.curto, conta, base, alvo, cmvManual,
    setCmv: (mlb, v) => setCmvManualMap((m) => { const n = new Map(m); if (v == null) n.delete(mlb); else n.set(mlb, v); return n }),
    live, escreve, statusLocal, marcar, desmarcar, marcarLote,
    sel, toggleSel: (k, on) => setSel((s) => { const n = new Set(s); if (on) n.add(k); else n.delete(k); return n }), limparSel: () => setSel(new Set()),
    pickerVal, setPicker: (k, v) => setPickerVal((m) => { const n = new Map(m); n.set(k, v); return n }),
    fila, setFila,
    abrirAnuncio: (mlb) => { setMlbSel(mlb); setAba('anuncio'); window.scrollTo({ top: 0, behavior: 'smooth' }) },
    abrirRobo: () => setAba('aprovacoes'),
    abrirModal: (a, p) => setModal({ a, p }),
    ativar, remover, freteReal, recarregarBase: recarregarSilencioso,
  } : null

  const aplicarAlvo = () => {
    const v = parseFloat(alvoTxt.replace(',', '.'))
    if (Number.isNaN(v) || v < 0 || v > 90) { setAlvoTxt(String(alvo)); return }
    setAlvo(v); gravarLS('fiesta_promo_alvo_mc', String(v))
  }

  const subtitle = fonte && conta ? `${subtituloFonte(fonte, `${metaConta.curto} (${metaConta.nome})`, conta.status_atualizado_em)} · ${conta.amostra}` : 'Promoções do Mercado Livre · margem por preço (não média)'

  return (
    <DomainPageShell title="Promoções ML" subtitle={subtitle} loading={loading} error={error} onRetry={() => void carregar()}>
      <Stack spacing={2.5}>
        <Card>
          <CardContent>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }} justifyContent="space-between">
              <Tabs value={contaId} onChange={(_, v: string) => setContaId(v)} variant="scrollable" allowScrollButtonsMobile sx={{ minHeight: 36 }}>
                {CONTAS.map((c) => <Tab key={c.id} value={c.id} label={`${c.curto} · ${c.nome}`} sx={{ minHeight: 36, py: 0.5, fontFamily: '"Outfit", sans-serif' }} />)}
              </Tabs>
              <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
                <ModoTag live={live} />
                <Button size="small" variant="outlined" color="inherit" disabled={!escreve || Boolean(atualizando)} onClick={() => void atualizarAgora()}
                  title={escreve ? 'Relê a API do Mercado Livre agora (status de cada promoção — ativa, candidata, encerrada —, preço e estoque de cada anúncio, todas as campanhas disponíveis; ~5 min nas duas contas), reaplica as regras do motor e republica este painel inteiro. NÃO escreve no ML e NÃO recalcula a M.C. (isso é a coleta completa das 02:30). Igual ao botão da aba Robô.' : 'Precisa do serviço de Promoções no ar.'}>
                  {atualizando ? `⏳ ${atualizando}` : '↻ Atualizar agora (reler a API)'}
                </Button>
                {avisoAtualizacao ? <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{avisoAtualizacao}</Typography> : null}
                <TextField size="small" label="Margem alvo %" value={alvoTxt} onChange={(e) => setAlvoTxt(e.target.value)} onBlur={aplicarAlvo} onKeyDown={(e) => { if (e.key === 'Enter') aplicarAlvo() }}
                  title="Margem de contribuição que você considera aceitável. Abaixo disso a M.C. aparece em amarelo; acima, em verde." slotProps={{ htmlInput: { inputMode: 'decimal', style: { width: 56, textAlign: 'right' } } }} />
                <ToggleButtonGroup exclusive size="small" value={base} onChange={(_, v: BaseImposto | null) => { if (v) setBase(v) }} title="A MC ajustada é a referência de entrada e saída (o motor decide nela). A oficial é só para comparar e volta para ajustada ao recarregar a tela.">
                  <ToggleButton value="ajustada">{ROTULO_BASE.ajustada} · decide</ToggleButton>
                  <ToggleButton value="real">{ROTULO_BASE.real} · só comparar</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Stack>
            {conta ? <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>{explicaBase(conta, base)}</Typography> : null}
            {base === 'real' ? <Alert severity="warning" sx={{ mt: 1.5, '& .MuiAlert-message': { fontSize: 13 } }}><b>Você está vendo a MC oficial (notas fiscais), só para comparação.</b> A referência de entrada e saída do robô é a <b>MC ajustada</b> — clique em "{ROTULO_BASE.ajustada} · decide" para voltar. A fila de decisões e o rodapé de regras continuam na ajustada.</Alert> : null}
          </CardContent>
        </Card>

        <Alert severity={escreve ? (live?.dry_run ? 'warning' : 'error') : 'info'} sx={{ '& .MuiAlert-message': { fontSize: 13 } }}>
          {escreve
            ? live?.dry_run
              ? 'Ambiente de teste: Participar, SAIR e as aprovações em massa rodam de ponta a ponta, mas nenhuma chamada chega ao Mercado Livre (fica em escritas_promo.log marcada [SIMULADO]).'
              : 'Escrita real: Participar, SAIR e as aprovações em massa escrevem na API do Mercado Livre depois de uma confirmação em dois cliques. Tudo fica registrado em escritas_promo.log.'
            : 'Preview: o serviço de escrita não respondeu, então nada é enviado ao Mercado Livre — os botões só marcam na tela.'}{' '}
          Base de imposto padrão: {ROTULO_BASE.ajustada} (o motor decide nesta). CMV digitado na aba Por anúncio é correção de leitura e não corrige a base.
        </Alert>

        {conta ? (
          <Grid container spacing={1.5}>
            {kpiList.map((k) => (
              <Grid key={k.label} size={{ xs: 6, sm: 4, md: 3, lg: 'grow' }}>
                <KpiCard label={k.label} value={k.value} hint={k.hint} valueTone={k.alerta ? 'caveat' : 'default'} loading={loading} />
              </Grid>
            ))}
          </Grid>
        ) : null}

        {conta && ctxValue ? (
          <PromoCtx.Provider value={ctxValue}>
            <Card>
              <CardContent sx={{ pb: 0 }}>
                <Tabs value={aba} onChange={(_, v: Aba) => setAba(v)} variant="scrollable" allowScrollButtonsMobile>
                  {ABAS.map(([k, l]) => <Tab key={k} value={k} label={l} />)}
                </Tabs>
              </CardContent>
              <Box sx={{ p: { xs: 1.5, sm: 2.25 } }}>
                {aba === 'aprovacoes' ? <AbaAprovacoes /> : null}
                {aba === 'promocoes' ? <AbaPromocoes /> : null}
                {aba === 'anuncio' ? <AbaAnuncio mlbSel={mlbSel} setMlbSel={setMlbSel} /> : null}
                {aba === 'analise' ? <AbaAnalise /> : null}
                {aba === 'alertas' ? <AbaAlertas /> : null}
                {aba === 'simulador' ? <AbaSimulador mlbSel={mlbSel} setMlbSel={setMlbSel} /> : null}
                {aba === 'historico' ? <AbaHistorico /> : null}
              </Box>
            </Card>
            <RodapeRegras />
            {modal ? <ModalParticipar key={selKey(modal.a, modal.p)} a={modal.a} p={modal.p} onClose={() => setModal(null)} /> : null}
          </PromoCtx.Provider>
        ) : null}

        {conta ? (
          <PropostaIaCard title="Leitura das Promoções" caption="Verdade = números da tela (KPIs e tabelas). Proposta = leitura da IA — revise antes de agir." state={leituraIa} onRetry={() => void carregarLeitura()} />
        ) : null}

        {conta ? (
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            Fonte: API do Mercado Livre (/seller-promotions/items, /sites/MLB/listing_prices e /users/&#123;seller&#125;/shipping_options/free no preço de cada promoção), coletada em {conta.fetched_at}. CMV: mongodb_financial.cmvs (nota fiscal → cmvs por SKU → custo do pedido). Imposto: margens {conta.periodo_margem?.de} a {conta.periodo_margem?.ate}, type "pedido", regra da skill margem-ajustada-fiesta. Nenhum dado de comprador nesta tela. Nenhum número é margem média.
          </Typography>
        ) : null}

        {loading ? <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}><CircularProgress /></Box> : null}
      </Stack>
    </DomainPageShell>
  )
}
